/**
 * AssigneeFieldset - Shared fieldset molecule for selecting a primary assignee
 * and optionally adding additional assignees.
 *
 * Used by the JobForm organism for both work order and inspection creation.
 * Handles:
 *	 - Primary assignee AsyncSearchableSelect over people, with neither a
 *	   role filter nor a company filter. Both form types search the same
 *	   pool: everyone the requester is allowed to see. The record's company
 *	   is the CUSTOMER being serviced, while the people it gets assigned to
 *	   are our own field staff — members of the service-provider company,
 *	   not of the customer — so narrowing the picker to the record's company
 *	   hid almost everyone. /users already applies the requester's own
 *	   company access scope, and the write side (CompanyScoped::
 *	   assertAssignable) accepts exactly the people that endpoint returns.
 *	   Only active people are offered: a deactivated person can't sign in
 *	   to do the work, and the server now refuses them as new assignees.
 *	 - People context per dropdown row: role + current open-job count
 *	   (via /users?with_workload=1) so the person assigning can weigh who
 *	   does what and how loaded they already are
 *	 - Additional assignees with chip display and remove button (work orders only)
 *	 - Name cache so chip labels and the primary selection display correctly
 *	   on edit-mode mount, even before the user opens the dropdown
 *	 - A failed lookup says so. "No people found. Add them under Admin →
 *	   Users." is a claim about the data; when the request itself fails it
 *	   sends the person off to create staff who are already on file. The two
 *	   cases now read differently.
 *	 - Subcontractor distinction: external vendors carry the 'subcontractor'
 *	   role; the dropdown labels and selected chips append " — Subcontractor"
 *	   so managers can tell internal employees from third-party vendors at a
 *	   glance without a separate dropdown
 *
 * When formType is 'inspection', the additional assignees UI is hidden since
 * inspections only support a single assignee.
 *
 * The primary picker can itself be hidden with showPrimary={false}. The field
 * workflow's Job Details panel does that: a tech on site adds the helpers who
 * turned up with them, while who OWNS the job stays a dispatch decision made
 * off the truck. Its labels are overridable (additionalLabel /
 * additionalHint) so the same control can read as "Also On Site" there and
 * "Additional Assignees" on the create/edit form.
 *
 * Quick add: given `quickAdd`, both pickers offer "+ Quick Add User" — as a
 * link beside "Add Assignee" (under the primary picker on an inspection) and
 * as an action row inside each dropdown, which also carries over whatever was
 * typed as the new person's name. People created from the primary picker take
 * the primary slot; from anywhere else they join the crew (primary first if
 * nobody is assigned yet) — the same rule the hours editor uses.
 *
 * Scales to thousands of users: only the search results are loaded
 * (default 25 per request, debounced 300ms), and already-selected names are
 * resolved one-shot via /users/{id} on mount.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../../services/api';
import { AsyncSearchableSelect, AsyncSearchableSelectOption } from '../AsyncSearchableSelect';
import { Chip } from '../../atoms';
import { dedupeAndSort } from '../../../utils/dedupeAndSort';
import { formatAssigneeLabel, formatRoleLabel } from '../../../utils/formatLabel';
import { addPeopleToCrew } from '../../../utils/quickAddUser';
import type { QuickAddedPersonRef, QuickAddOptions } from '../../../utils/quickAddUser';
import { QuickAddUserLink, useQuickAddFooterAction } from '../QuickAddUserLink';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** User record shape returned by /users (snake_case Laravel).
 *  `role` is read so the picker can label subcontractors distinctly. */
interface DropdownUser {
	id: string | number;
	first_name: string;
	last_name: string;
	/** Optional Laravel-side role on the user record. Read here so the
	 *  picker can append " — Subcontractor" to external-vendor labels. */
	role?: string;
	/** Subcontractor's business name — shown in the label. */
	subcontractor_company?: string | null;
	/** Open (not completed/cancelled) counts, present when the list was
	 *  fetched with with_workload=1. Summed into the "N open jobs" hint. */
	open_work_orders_count?: number;
	open_inspections_count?: number;
}

/* Every active person the requester can see is assignable — the picker
 * carries neither a role filter nor a company filter. The server-side rule is
 * CompanyScoped::assertAssignable, which accepts any user /users would have
 * returned to this requester and refuses deactivated ones, so the picker
 * mirrors it by asking /users for status=active and nothing narrower. */

/** Props for the AssigneeFieldset molecule */
export interface AssigneeFieldsetProps {
	/** Which form type this fieldset is in -- determines if additional assignees are shown */
	formType: 'work_order' | 'inspection';
	/** Currently selected primary assignee user ID */
	assigneeId: string;
	/** Callback when the primary assignee changes */
	onAssigneeChange: (id: string) => void;
	/** Array of additional technician IDs (work orders only) */
	additionalAssigneeIds: string[];
	/** Callback when additional assignees change (work orders only) */
	onAdditionalAssigneesChange: (ids: string[]) => void;
	/**
	 * Whether to render the primary-assignee picker. Defaults to true (the
	 * create/edit form, where picking the primary is the point). The field
	 * workflow's Job Details panel passes false: a tech on site adds the
	 * helpers who turned up with them, but who OWNS the job is a dispatch
	 * decision made off the truck. Additional assignees still work either way.
	 */
	showPrimary?: boolean;
	/**
	 * Show the primary assignee but don't let it change. Moving a work order
	 * to someone else needs the Can Reassign Work Orders permission, and the
	 * server refuses it without; the edit form passes this for anyone who
	 * lacks it, so they aren't offered a change that can't be saved. The
	 * additional assignees stay editable either way.
	 */
	primaryLocked?: boolean;
	/** Overrides the "Additional Assignees" label — the field panel calls the
	 *  same list "People On This Job", which is what a crew calls it. */
	additionalLabel?: string;
	/** Overrides the muted helper line under the additional-assignee control. */
	additionalHint?: string;
	/** Offer "+ Quick Add User" on these pickers. `companyId` is the record's
	 *  business, pre-selected as each new person's primary business. */
	quickAdd?: QuickAddOptions;
	/** Inline validation errors keyed by field name */
	errors?: Record<string, string>;
	/** Standard label CSS class for consistent styling */
	labelClass: string;
	/** Optional extra classes appended to the root element. */
	className?: string;
}

/* ============================================
 * HELPERS
 * ============================================ */

/** Display label for the picker, the selected-state input and the
 *  additional-assignee chips — subcontractors are marked (with their business
 *  name when set) so external vendors stand apart from employees. Shared with
 *  the hours rows via utils/formatLabel. */
const displayLabel = (u: DropdownUser): string => formatAssigneeLabel(u);

/** Muted context line under each dropdown row: the person's role plus their
 *  current open-job load ("Technician · 3 open jobs"). Subcontractor rows
 *  already carry the role in the label suffix, so they get workload only.
 *  Workload renders only when the API was asked for it (with_workload=1). */
const contextSublabel = (u: DropdownUser): string | undefined => {
	const parts: string[] = [];
	if (u.role && u.role !== 'subcontractor') {
		parts.push(formatRoleLabel(u.role));
	}
	if (u.open_work_orders_count != null || u.open_inspections_count != null) {
		const open = (u.open_work_orders_count ?? 0) + (u.open_inspections_count ?? 0);
		parts.push(`${open} open job${open === 1 ? '' : 's'}`);
	}
	return parts.length > 0 ? parts.join(' · ') : undefined;
};

/** Map a /users API row to an AsyncSearchableSelect option */
const userToOption = (u: DropdownUser): AsyncSearchableSelectOption => ({
	value: String(u.id),
	label: displayLabel(u),
	sublabel: contextSublabel(u),
});

/* ============================================
 * COMPONENT
 * ============================================ */

export const AssigneeFieldset: React.FC<AssigneeFieldsetProps> = ({
	formType,
	assigneeId,
	onAssigneeChange,
	additionalAssigneeIds,
	onAdditionalAssigneesChange,
	showPrimary = true,
	primaryLocked = false,
	additionalLabel = 'Additional Assignees',
	additionalHint = 'Optionally assign additional people to this work order.',
	quickAdd,
	errors = {},
	labelClass,
	className = '',
}) => {
	/* Cache of id → display label for people the component has seen. Populated
	   by (a) the /users/{id} resolver below for IDs already selected on mount,
	   and (b) the onChange callbacks when the user picks from the dropdown.
	   Labels only — the picker no longer filters on company, so there is
	   nothing left to re-validate a cached selection against. */
	const [userCache, setUserCache] = useState<Record<string, string>>({});
	const [showAdditionalSelect, setShowAdditionalSelect] = useState(false);

	/* Resolve display names for any selected IDs that aren't in the cache yet.
	   Runs on mount and whenever the selected IDs change to something we
	   haven't resolved before. */
	useEffect(() => {
		const ids = [assigneeId, ...additionalAssigneeIds].filter(Boolean);
		const missing = ids.filter((id) => !(id in userCache));
		if (missing.length === 0) return;

		let cancelled = false;
		(async () => {
			const results = await Promise.all(
				missing.map(async (id): Promise<[string, string]> => {
					try {
						const res = await api.get<any>(`/users/${id}`);
						const u: DropdownUser | undefined = res.data?.data ?? res.data;
						if (u && u.id != null) {
							/* displayLabel preserves the " — Subcontractor" suffix
							   on edit-mode mount so the chip / input matches what
							   the user sees in the dropdown. */
							return [id, displayLabel(u)];
						}
					} catch {
						/* Fall through — show the raw id as a last resort */
					}
					return [id, id];
				})
			);
			if (!cancelled) {
				setUserCache((prev) => {
					const next = { ...prev };
					for (const [id, label] of results) {
						next[id] = label;
					}
					return next;
				});
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [assigneeId, additionalAssigneeIds, userCache]);

	/* Server-side search across everyone the requester can see, with workload
	   context for the dropdown sublabels. Excludes the primary assignee and
	   already-added additional assignees so the user can't double-assign.
	   Deliberately sends no company_id — /users applies the requester's own
	   company access scope, and narrowing further to the record's customer
	   company is what emptied this dropdown. Results are alphabetized A-Z by
	   display name via dedupeAndSort, since the API guarantees no order. */
	const searchAssignableUsers = useCallback(async (search: string): Promise<AsyncSearchableSelectOption[]> => {
		const params: Record<string, string> = { search, with_workload: '1', status: 'active' };
		const res = await api.get<any>('/users', params);
		const data: DropdownUser[] = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
		const excludeIds = new Set<string>([assigneeId, ...additionalAssigneeIds].filter(Boolean));
		return dedupeAndSort(
			data
				.filter((u) => !excludeIds.has(String(u.id)))
				.map(userToOption)
		);
	}, [assigneeId, additionalAssigneeIds]);

	/* Search variant for the PRIMARY picker — excludes only the additional
	   assignees so the user can re-pick the current primary if desired.
	   Same pool + alphabetization as searchAssignableUsers above. */
	const searchPrimaryAssignableUsers = useCallback(async (search: string): Promise<AsyncSearchableSelectOption[]> => {
		const params: Record<string, string> = { search, with_workload: '1', status: 'active' };
		const res = await api.get<any>('/users', params);
		const data: DropdownUser[] = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
		const excludeIds = new Set<string>(additionalAssigneeIds.filter(Boolean));
		return dedupeAndSort(
			data
				.filter((u) => !excludeIds.has(String(u.id)))
				.map(userToOption)
		);
	}, [additionalAssigneeIds]);

	const handlePrimaryChange = useCallback((id: string, opt: AsyncSearchableSelectOption | null) => {
		if (opt) {
			setUserCache((prev) => prev[opt.value] ? prev : { ...prev, [opt.value]: opt.label });
		}
		onAssigneeChange(id);
	}, [onAssigneeChange]);

	const handleAddAdditional = useCallback((id: string, opt: AsyncSearchableSelectOption | null) => {
		if (id && !additionalAssigneeIds.includes(id)) {
			if (opt) {
				setUserCache((prev) => prev[opt.value] ? prev : { ...prev, [opt.value]: opt.label });
			}
			onAdditionalAssigneesChange([...additionalAssigneeIds, id]);
		}
		setShowAdditionalSelect(false);
	}, [additionalAssigneeIds, onAdditionalAssigneesChange]);

	const handleRemoveAdditional = useCallback((techId: string) => {
		onAdditionalAssigneesChange(additionalAssigneeIds.filter((id) => id !== techId));
	}, [additionalAssigneeIds, onAdditionalAssigneesChange]);

	/* ---- Quick add ---- */

	/** Remember new people's names so their chip / selection shows at once. */
	const cacheNames = useCallback((people: QuickAddedPersonRef[]) => {
		setUserCache((prev) => {
			const next = { ...prev };
			for (const person of people) next[person.id] = person.name;
			return next;
		});
	}, []);

	/** From the primary picker: the first new person takes the primary slot,
	 *  anyone else joins the additional crew (work orders only). */
	const handleQuickAddedPrimary = useCallback((people: QuickAddedPersonRef[]) => {
		if (people.length === 0) return;
		cacheNames(people);
		const [first, ...rest] = people;
		onAssigneeChange(first.id);
		if (formType === 'work_order' && rest.length > 0) {
			onAdditionalAssigneesChange(addPeopleToCrew(first.id, additionalAssigneeIds.filter((id) => id !== first.id), rest).additionalIds);
		}
	}, [cacheNames, formType, additionalAssigneeIds, onAssigneeChange, onAdditionalAssigneesChange]);

	/** From the additional picker / link: join the crew, primary first when empty
	 *  (unless the primary picker is hidden, where the slot isn't ours to fill). */
	const handleQuickAddedCrew = useCallback((people: QuickAddedPersonRef[]) => {
		if (people.length === 0) return;
		cacheNames(people);
		/* A locked primary is never ours to fill, even when it's empty. */
		const canFillPrimary = showPrimary && !primaryLocked;
		const crew = addPeopleToCrew(assigneeId, additionalAssigneeIds, people, { fillPrimary: canFillPrimary });
		if (canFillPrimary && crew.primaryId !== assigneeId) onAssigneeChange(crew.primaryId);
		if (crew.additionalIds.length !== additionalAssigneeIds.length) onAdditionalAssigneesChange(crew.additionalIds);
		setShowAdditionalSelect(false);
	}, [cacheNames, showPrimary, primaryLocked, assigneeId, additionalAssigneeIds, onAssigneeChange, onAdditionalAssigneesChange]);

	const primaryFooterAction = useQuickAddFooterAction(quickAdd ? { defaultCompanyId: quickAdd.companyId, onCreated: handleQuickAddedPrimary, single: formType === 'inspection' } : null);
	const crewFooterAction = useQuickAddFooterAction(quickAdd ? { defaultCompanyId: quickAdd.companyId, onCreated: handleQuickAddedCrew } : null);

	const getAssigneeName = useCallback((userId: string): string => {
		return userCache[userId] || 'Loading...';
	}, [userCache]);

	return (
		<div className={`assignee-fieldset space-y-16px ${className}`} data-ref="molecule-assignee-fieldset">
			{/* Primary assignee — server-search typeahead. Hidden when the
			    caller only wants the crew list (showPrimary={false}). */}
			{showPrimary && (
			<div className="assignee-fieldset__primary">
				<label htmlFor="assigneeId" className={labelClass}>
					{formType === 'work_order' ? 'Assign To' : 'Assignee'} <span className="text-red-500">*</span>
				</label>
				<AsyncSearchableSelect
					id="assigneeId"
					value={assigneeId}
					selectedLabel={assigneeId ? userCache[assigneeId] : ''}
					onChange={handlePrimaryChange}
					fetchOptions={searchPrimaryAssignableUsers}
					placeholder="Search people..."
					hasError={!!errors.assigneeId}
					emptyMessage={quickAdd ? 'No people found.' : 'No people found. Add them under Admin → Users.'}
					errorMessage="Couldn't load people. Check your connection and try again."
					footerAction={primaryLocked ? undefined : primaryFooterAction}
					disabled={primaryLocked}
				/>
				{errors.assigneeId && <p className="mt-4px text-14px text-red-600">{errors.assigneeId}</p>}
				{primaryLocked && (
					<p className="assignee-fieldset__primary-note mt-4px text-12px font-sans text-brand-text-muted">Changing who this job is assigned to needs the Can Reassign Work Orders permission.</p>
				)}
				{/* An inspection has no additional-assignee row to sit beside,
				    so its quick-add link lives under the primary picker. */}
				{quickAdd && formType === 'inspection' && !primaryLocked && (
					<QuickAddUserLink defaultCompanyId={quickAdd.companyId} onCreated={handleQuickAddedPrimary} single className="mt-4px" />
				)}
			</div>
			)}

			{/* Additional assignees -- only shown for work orders */}
			{formType === 'work_order' && (
				<div className="assignee-fieldset__additional">
					<label className={labelClass}>{additionalLabel}</label>

					{/* Chip display for each additional assignee with remove button.
					    Subcontractor chips render the role suffix from the cached
					    label so external vendors stand out from technicians. */}
					{additionalAssigneeIds.length > 0 && (
						<div className="flex flex-wrap gap-8px mb-8px">
							{additionalAssigneeIds.map((userId) => (
								<Chip key={userId} label={getAssigneeName(userId)} onRemove={() => handleRemoveAdditional(userId)} dataRef="molecule-assignee-fieldset-chip" />
							))}
						</div>
					)}

					{/* "Add Assignee" and, beside it, "+ Quick Add User" for someone
					    who isn't in the system yet. */}
					{!showAdditionalSelect && (
						<div className="assignee-fieldset__additional-actions flex flex-wrap items-center gap-x-16px">
							<button type="button" onClick={() => setShowAdditionalSelect(true)} className="inline-flex items-center gap-4px min-h-44px text-14px font-sans font-medium text-brand-primary hover:text-brand-primary-hover transition-colors">
								<svg className="h-16px w-16px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
								Add Assignee
							</button>
							{quickAdd && <QuickAddUserLink defaultCompanyId={quickAdd.companyId} onCreated={handleQuickAddedCrew} />}
						</div>
					)}

					{showAdditionalSelect && (
						<div className="mt-8px">
							<AsyncSearchableSelect
								id="additionalAssignee"
								value=""
								onChange={handleAddAdditional}
								fetchOptions={searchAssignableUsers}
								placeholder="Search people..."
								emptyMessage="No more people to add."
								errorMessage="Couldn't load people. Check your connection and try again."
								footerAction={crewFooterAction}
							/>
							<button type="button" onClick={() => setShowAdditionalSelect(false)} className="inline-flex items-center mt-4px min-h-44px text-12px font-sans text-brand-text-muted hover:text-brand-text transition-colors">Cancel</button>
						</div>
					)}

					<p className="mt-4px text-12px font-sans text-brand-text-muted">{additionalHint}</p>
				</div>
			)}
		</div>
	);
};

export default AssigneeFieldset;
